<template>
	<div
		class="captcha"
		:class="{ captcha_done: !!value, captcha_failed: failed }"
	>
		<span class="captcha__mark" aria-hidden="true">
			<span v-if="value" class="captcha__check">✓</span>
			<span v-else-if="solving" class="captcha__spinner"></span>
		</span>
		<span class="captcha__text">{{ statusText }}</span>
		<button
			v-if="failed"
			type="button"
			class="captcha__retry"
			@click="solve"
		>
			{{ $t('Повторить') }}
		</button>
	</div>
</template>

<script>
/**
 * Капча ALTCHA (altcha.org) — proof-of-work, всё своё, без внешних сервисов.
 * Бэк отдаёт задачу, здесь подбираем число, у которого sha256(salt + число)
 * совпадает с challenge, и отправляем результат вместе с формой.
 */
export default {
	name: 'Captcha',
	props: {
		value: {
			type: String,
			default: '',
		},
	},
	data() {
		return {
			solving: false,
			failed: false,
		};
	},
	computed: {
		statusText() {
			if (this.value) {
				return this.$t('Проверка пройдена');
			}
			if (this.failed) {
				return this.$t('Проверка не удалась');
			}
			return this.$t('Проверяем, что вы не робот…');
		},
	},
	mounted() {
		this.solve();
	},
	methods: {
		async solve() {
			if (this.solving) {
				return;
			}

			this.solving = true;
			this.failed = false;
			this.$emit('input', '');

			try {
				const { data } = await this.$http.get('altcha/challenge');
				const number = await this.findNumber(data);

				if (number === null) {
					throw new Error('solution not found');
				}

				this.$emit(
					'input',
					btoa(
						JSON.stringify({
							algorithm: data.algorithm,
							challenge: data.challenge,
							number,
							salt: data.salt,
							signature: data.signature,
						}),
					),
				);
			} catch (e) {
				this.failed = true;
			} finally {
				this.solving = false;
			}
		},

		async findNumber({ salt, challenge, maxnumber }) {
			const encoder = new TextEncoder();

			for (let n = 0; n <= maxnumber; n++) {
				const buffer = await crypto.subtle.digest(
					'SHA-256',
					encoder.encode(salt + n),
				);

				const hex = Array.from(new Uint8Array(buffer))
					.map((b) => b.toString(16).padStart(2, '0'))
					.join('');

				if (hex === challenge) {
					return n;
				}

				// раз в тысячу итераций отдаём поток браузеру, чтобы не морозить UI
				if (n % 1000 === 0) {
					await new Promise((resolve) => setTimeout(resolve, 0));
				}
			}

			return null;
		},
	},
};
</script>

<style scoped lang="scss">
.captcha {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px 12px;
	border-radius: 10px;
	border: 1px solid var(--outer-space);
	background-color: var(--heavy-metal);
	font-family: var(--font-1);
	font-size: 13px;
	line-height: 18px;
	color: var(--pumice);

	&_done {
		color: var(--gray-nurse);
	}

	&_failed {
		border-color: #b3444a;
	}

	&__mark {
		flex: 0 0 18px;
		width: 18px;
		height: 18px;
		border-radius: 5px;
		border: 1px solid var(--outer-space);
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&__check {
		font-size: 12px;
		line-height: 1;
		color: #5cc98a;
	}

	&__spinner {
		width: 10px;
		height: 10px;
		border-radius: 50%;
		border: 2px solid var(--outer-space);
		border-top-color: var(--gray-nurse);
		animation: captcha-spin 0.8s linear infinite;
	}

	&__retry {
		margin-left: auto;
		background: transparent;
		border: 0;
		padding: 0;
		color: var(--gray-nurse);
		font-size: 13px;
		text-decoration: underline;
		cursor: pointer;
	}
}

@keyframes captcha-spin {
	to {
		transform: rotate(360deg);
	}
}
</style>
